import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AiPreviewScreen, CampaignMediaItem, CreativePreview } from '@/features/campaigns/types/campaign';
import { MAX_CAMPAIGN_AMOUNT } from '@/features/campaigns/constants/campaignOptions';
import {
  useAiGeneratePreview,
  useSaveAiCampaign,
} from '@/features/campaigns/api/useCampaignMutations';
import { fetchAvailableCoupons } from '@/features/campaigns/api/couponApi';
import { couponDiscount as discountOnOrder, rankOffers } from '@/features/campaigns/utils/couponMath';
import { buildCampaignName } from '@/features/campaigns/utils/campaignName';
import { emptyManualForm } from '@/features/campaigns/types/campaign';
import { sanitizeCampaignName, creativeSourcePayload } from '@/features/campaigns/utils/campaignUtils';
import {
  loadAiCampaignDraft,
  patchAiCampaignDraft,
} from '@/features/campaigns/utils/aiCampaignDraftStorage';
import { fetchWalletBalance } from '@/features/dashboard/api/dashboardApi';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { getErrorMessage } from '@/shared/api/errors';
import { formatMoney } from '@/shared/lib/format/money';
import { useAppSelector } from '@/shared/store/hooks';
import { isTeamMemberRole } from '@/shared/config/roles';
import { useAiPreviewDerived } from './useAiPreviewDerived';
import { estimateAiPlan, aiScreenKey, perPlayPrice } from './aiPriceEstimate';
import {
  applyStep3ToPlan,
  addScreenToPlan,
  bookingSlotsForPlays,
  planDates,
  planDays,
  playsPerDayByScreen,
} from './aiPlanFromSelection';
import { campaignStep2, campaignStep3, fetchScreenCapacity } from '@/features/campaigns/api/campaignApi';
import { fetchAllBillboards } from '@/features/billboards/api/billboardApi';
import type { ScreenListItem } from '@/features/campaigns/components/ScreensStep/ScreensStep.types';
import { useAiPlanCustomisation } from './useAiPlanCustomisation';
import type { AIPreviewProps, AiPreviewOrientation } from './AIPreview.types';

export function useAiPreview({
  aiData,
  formData,
  onLaunchSuccess,
  onCampaignCompleted,
  onAiDataChange,
}: AIPreviewProps) {
  const isTeamMember = isTeamMemberRole(
    useAppSelector((s) => s.auth.user?.role),
  );
  const draft = useMemo(() => loadAiCampaignDraft(), []);
  const [current, setCurrent] = useState(aiData);

  useEffect(() => {
    setCurrent(aiData);
  }, [aiData]);

  // A name that identifies the campaign by itself: business initials, today's date, a random code.
  const user = useAppSelector((s) => s.auth.user) as
    | { name?: string; firstName?: string; lastName?: string }
    | null
    | undefined;
  const [campaignName, setCampaignName] = useState(
    () =>
      draft?.campaignName ||
      buildCampaignName({
        brand: formData.brand,
        company: formData.company,
        ownerName: user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' '),
      }),
  );
  const [couponCode, setCouponCode] = useState(draft?.couponCode || '');
  const [uploads, setUploads] = useState<
    Partial<Record<AiPreviewOrientation, CampaignMediaItem>>
  >({});
  const [previewByOrientation, setPreviewByOrientation] = useState<
    Partial<Record<AiPreviewOrientation, CreativePreview>>
  >({});
  const [depositOpen, setDepositOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showAllSlots, setShowAllSlots] = useState(false);
  const save = useSaveAiCampaign();
  const regenerate = useAiGeneratePreview();

  const walletQuery = useQuery({
    queryKey: queryKeys.wallet.balance(),
    queryFn: fetchWalletBalance,
    refetchOnWindowFocus: false,
  });
  const availableBalance = Number(
    walletQuery.data?.data?.availableBalance ?? 0,
  );

  // Every offer this user can use, asked for once at the largest order size. Offers are then ranked
  // and priced here for whatever the order is at the moment, so applying one is instant.
  const couponsQuery = useQuery({
    queryKey: ['ai-coupons'],
    staleTime: 60_000,
    queryFn: async () => (await fetchAvailableCoupons(MAX_CAMPAIGN_AMOUNT)).data ?? [],
  });

  const {
    estimatedCost,
    screens,
    screenNameById,
    namesForSlot,
    fallbackScreenNames,
    dateRange,
    durationSec,
    totalScreens,
    totalRepetitions,
    amountWithTax,
    amountWithoutTax,
    remaining,
    utilization,
    appliedDiscount,
    summary,
    taxRows,
    planBudget,
  } = useAiPreviewDerived(current, formData);

  const planCost = Number(
    amountWithTax ??
      summary?.amountWithTax ??
      estimatedCost?.finalPayableAmount ??
      estimatedCost?.total ??
      summary?.amountWithoutTax ??
      0,
  );

  /** The orientations the current plan covers (one, or both), landscape first. */
  const planOrientations = useMemo(() => {
    const wanted = new Set((current.requiredOrientation ?? []).map((o) => String(o).toUpperCase()));
    const list = (['LANDSCAPE', 'PORTRAIT'] as AiPreviewOrientation[]).filter((o) => wanted.has(o));
    return list.length ? list : (['LANDSCAPE'] as AiPreviewOrientation[]);
  }, [current.requiredOrientation]);

  /** What the AI recommended for the brief. It stays put when the user picks other orientations. */
  const aiOrientation = (current.aiRecommendedOrientation ?? planOrientations[0]) as AiPreviewOrientation;

  const days = useMemo(() => planDays(current), [current]);
  const planPlays = useMemo(() => {
    const byId = playsPerDayByScreen(current);
    return Object.fromEntries(screens.map((s) => [aiScreenKey(s), byId[String(s._id)] ?? 0]));
  }, [current, screens]);

  const customisation = useAiPlanCustomisation(screens, planOrientations, planPlays, Boolean(current.pendingSave));

  // The coupon the user has picked. If it is not one of the listed offers (for example an offer the
  // server applied by itself), the plan's own discount is scaled to the order instead.
  const selectedCoupon = (couponsQuery.data ?? []).find(
    (coupon) => String(coupon.couponCode).toUpperCase() === couponCode.trim().toUpperCase(),
  );
  const serverCouponCode = String(current.coupon?.couponCode ?? '').trim().toUpperCase();
  const discountFor = (base: number) => {
    if (selectedCoupon) return discountOnOrder(selectedCoupon, base);
    const planBase = Number(amountWithoutTax ?? 0);
    if (serverCouponCode && couponCode.trim().toUpperCase() === serverCouponCode && planBase > 0) {
      return Number(appliedDiscount || 0) * Math.min(1, base / planBase);
    }
    return 0;
  };

  const estimate = estimateAiPlan({
    screens,
    selectedKeys: customisation.selectedKeys,
    playsByKey: customisation.playsByKey,
    days,
    discountFor,
    budget: planBudget,
  });

  // A picked coupon that no longer applies (the order dropped below its minimum) is not sent.
  const activeCouponCode = selectedCoupon && estimate.discount <= 0 ? '' : couponCode.trim();
  const couponChanged = activeCouponCode.toUpperCase() !== serverCouponCode;

  // Anything the server has not seen yet: screens, plays, or a coupon.
  const needsSave = customisation.needsSave || couponChanged;
  // Until the user changes something, show the server's own total; afterwards the live estimate.
  const cost = needsSave ? estimate.total : planCost;

  // ── More screens the user can add ──────────────────────────────────────────────────────────
  const catalogCities = useMemo(
    () => ((current.payload?.targetCities as string[] | undefined) ?? formData.targetCities ?? []).filter(Boolean),
    [current.payload, formData.targetCities],
  );
  const catalogQuery = useQuery({
    queryKey: ['ai-plan-catalog', catalogCities, planOrientations.join(',')],
    enabled: catalogCities.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      // One list per city and orientation, so a plan covering both offers screens of both.
      const lists = await Promise.all(
        catalogCities.flatMap((city) =>
          planOrientations.map((screenOrientation) =>
            fetchAllBillboards({ city, screenOrientation, limit: 50 }),
          ),
        ),
      );
      return lists.flatMap((res) => (res?.data?.billboards ?? []) as ScreenListItem[]);
    },
  });

  const inPlanIds = new Set(screens.map((s) => String(s._id)));
  const addableScreens = useMemo(
    () =>
      (catalogQuery.data ?? [])
        .filter((item) => !inPlanIds.has(String(item._id ?? item.id)))
        .sort((a, b) => Number(a.basePrice ?? 0) - Number(b.basePrice ?? 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogQuery.data, screens],
  );

  /** GST as a share of the subtotal, taken from this plan (about 18% for Madhya Pradesh). */
  const gstRatio = useMemo(() => {
    const sub = Number(current.summary?.amountWithoutTax ?? 0);
    const tax = Number(current.summary?.totalTax ?? 0);
    return sub > 0 && tax > 0 ? tax / sub : 0.18;
  }, [current.summary]);
  const adSeconds = Number(durationSec ?? formData.adDuration ?? 30) || 30;
  const [addingScreenId, setAddingScreenId] = useState<string | null>(null);

  /** Rough price of one play on one day for a screen not yet in the plan. */
  const estimatePlayPrice = (item: ScreenListItem) =>
    Number(item.basePrice ?? 0) * adSeconds * (1 + gstRatio);

  // Budget left in the bill, which includes anything a coupon saved.
  const remainingBudget = planBudget != null ? planBudget - estimate.total : null;
  // Budget the user chose not to put into plays. Money a coupon saved does not count: a discount
  // is a reason to pay less, so it must never lock the Pay button.
  const unusedBudget = remainingBudget != null ? remainingBudget - estimate.couponSavings : null;

  // ── Free plays per screen and hour (for the plan's screens and the cheapest ones to add) ──
  const dates = useMemo(() => planDates(current), [current]);
  const capacityIds = useMemo(
    () => [
      ...new Set([
        ...screens.map((s) => String(s._id)),
        ...addableScreens.slice(0, 12).map((item) => String(item._id ?? item.id)),
      ]),
    ].filter(Boolean),
    [screens, addableScreens],
  );
  const capacityQuery = useQuery({
    queryKey: ['ai-screen-capacity', capacityIds, dates, adSeconds],
    enabled: capacityIds.length > 0 && dates.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fetchScreenCapacity({ billboardIds: capacityIds, dates, customDuration: adSeconds });
      return res.data?.capacity ?? {};
    },
  });
  const [extraCapacity, setExtraCapacity] = useState<Record<string, Record<string, number>>>({});
  const capacity = useMemo(
    () => (capacityQuery.data ? { ...capacityQuery.data, ...extraCapacity } : undefined),
    [capacityQuery.data, extraCapacity],
  );
  const capacityTotals = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(capacity ?? {}).map(([id, hours]) => [id, Object.values(hours).reduce((a, b) => a + b, 0)]),
      ) as Record<string, number>,
    [capacity],
  );

  const addScreen = async (item: ScreenListItem, wantedPlays?: number) => {
    const id = String(item._id ?? item.id ?? '');
    if (!id || addingScreenId || planBudget == null) return;
    setAddingScreenId(id);
    try {
      let hoursFree = capacity?.[id];
      if (!hoursFree) {
        const res = await fetchScreenCapacity({ billboardIds: [id], dates, customDuration: adSeconds });
        hoursFree = res.data?.capacity?.[id] ?? {};
        setExtraCapacity((prev) => ({ ...prev, [id]: hoursFree as Record<string, number> }));
      }
      const freeByHour = new Map(Object.entries(hoursFree).map(([h, n]) => [Number(h), Number(n)]));
      if (!freeByHour.size) {
        toast.error('This screen has no free plays on your campaign days.');
        return;
      }

      // Prefer the hour your other screens already use, then daytime, then any free hour.
      const usedHours = new Map<number, number>();
      for (const slot of current.timeSlots ?? []) {
        usedHours.set(Number(slot.hour), (usedHours.get(Number(slot.hour)) ?? 0) + 1);
      }
      const hours = [...freeByHour.keys()].sort((a, b) => a - b);
      const hour =
        [...usedHours.entries()].sort((a, b) => b[1] - a[1]).map(([h]) => h).find((h) => freeByHour.has(h)) ??
        hours.find((h) => h >= 8 && h <= 20) ??
        hours[0];

      const grossPerPlayDay = estimatePlayPrice(item) * days;
      const affordable = grossPerPlayDay > 0 ? Math.floor(((remainingBudget ?? 0) + 0.005) / grossPerPlayDay) : 0;
      const dailyCapacity = [...freeByHour.values()].reduce((a, b) => a + b, 0);
      // Going over budget is allowed, so a screen is added with at least 1 play even if it costs more
      // than what is left; the user then agrees to the extra on the bill.
      const plays = Math.min(dailyCapacity, wantedPlays ?? Math.max(1, Math.min(affordable, 5)), freeByHour.get(hour) ?? 1);

      const place = [item.address?.area, item.address?.city].filter(Boolean).join(', ');
      const totalPlays = plays * days;
      const subtotal = Number(item.basePrice ?? 0) * adSeconds * totalPlays;
      const screen = {
        _id: id,
        screenName: item.screenName || item.name || 'Screen',
        address: { area: item.address?.area, city: item.address?.city, state: item.address?.state },
        screenType: item.screenType,
        basePrice: item.basePrice,
        description: `${item.screenName || 'This screen'}${place ? ` in ${place}` : ''}. Added by you.`,
        addedByUser: true,
        pricing: {
          totalRepetitions: totalPlays,
          amountWithoutTax: subtotal,
          totalTax: subtotal * gstRatio,
          amountWithTax: subtotal * (1 + gstRatio),
        },
      };

      const plan = addScreenToPlan(current, screen, hour, plays);
      setCurrent(plan);
      onAiDataChange?.(plan);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not add this screen'));
    } finally {
      setAddingScreenId(null);
    }
  };

  /** Most plays a day a screen in the plan can take; until capacity loads, no more than it has. */
  const capOf = (screen: AiPreviewScreen) =>
    capacityTotals[String(screen._id)] ?? (customisation.playsByKey[aiScreenKey(screen)] ?? 0);

  /** Puts leftover budget into extra plays, starting from the last screen in the list. */
  const planFill = () => {
    let headroom = unusedBudget ?? 0;
    const additions: Array<{ key: string; name: string; from: number; plays: number; cost: number }> = [];
    for (const screen of [...screens].reverse()) {
      const key = aiScreenKey(screen);
      if (!customisation.selectedKeys.has(key)) continue;
      const perDay = perPlayPrice(screen).gross * days;
      if (perDay <= 0) continue;
      const plays = customisation.playsByKey[key] ?? 0;
      const extra = Math.min(Math.floor((headroom + 0.005) / perDay), capOf(screen) - plays);
      if (extra > 0) {
        additions.push({
          key,
          name: screen.screenName || 'Screen',
          from: plays,
          plays: plays + extra,
          cost: extra * perDay,
        });
        headroom -= extra * perDay;
      }
    }
    return additions;
  };
  const fillPlan = unusedBudget != null ? planFill() : [];
  const canFillBudget = fillPlan.length > 0;
  const fillRemainingBudget = () => planFill().forEach(({ key, plays }) => customisation.setPlays(key, plays));

  /** Gives every chosen screen the same plays a day: the most that fits the budget and each screen. */
  const equalizePlays = () => {
    const chosen = screens.filter((s) => customisation.selectedKeys.has(aiScreenKey(s)));
    if (!chosen.length) return;
    const costPerEachPlay = chosen.reduce((sum, s) => sum + perPlayPrice(s).gross * days, 0);
    const byBudget =
      planBudget != null && costPerEachPlay > 0
        ? Math.floor((planBudget + 0.005) / costPerEachPlay)
        : Number.POSITIVE_INFINITY;
    const byCapacity = Math.min(...chosen.map((s) => capacityTotals[String(s._id)] ?? Number.POSITIVE_INFINITY));
    const plays = Math.max(1, Math.min(byBudget, byCapacity));
    chosen.forEach((s) => customisation.setPlays(aiScreenKey(s), plays));
  };

  /**
   * When the chosen screens cannot take the leftover budget, other screens that have free plays
   * and are affordable, with how many plays a day they can take.
   */
  const budgetSuggestions = useMemo(() => {
    if (unusedBudget == null || unusedBudget <= 0 || canFillBudget || !capacity) return [];
    return addableScreens
      .map((item) => {
        const id = String(item._id ?? item.id);
        const unit = Number(item.basePrice ?? 0) * adSeconds * (1 + gstRatio) * days;
        const free = capacityTotals[id] ?? 0;
        const plays = unit > 0 ? Math.min(Math.floor((unusedBudget + 0.005) / unit), free) : 0;
        return { item, plays, cost: plays * unit };
      })
      .filter((suggestion) => suggestion.plays >= 1)
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 3);
  }, [addableScreens, capacity, capacityTotals, unusedBudget, canFillBudget, adSeconds, gstRatio, days]);

  /** Budget is left and could buy more plays. */
  const hasUnusedBudget = canFillBudget || budgetSuggestions.length > 0;

  // The user may choose to spend less on purpose. Confirming holds only for this exact selection
  // of screens and plays; changing either asks again.
  const selectionKey = JSON.stringify([
    current.campaignId,
    [...customisation.selectedKeys].sort(),
    customisation.playsByKey,
  ]);
  const [confirmedKey, setConfirmedKey] = useState<string | null>(null);
  const unusedAccepted = confirmedKey === selectionKey;
  const setUnusedAccepted = (accepted: boolean) => setConfirmedKey(accepted ? selectionKey : null);

  // Going over budget is allowed once the user agrees to pay the difference. Like the confirm
  // above, it holds only for this exact selection.
  const [overConfirmedKey, setOverConfirmedKey] = useState<string | null>(null);
  const overBudgetAccepted = overConfirmedKey === selectionKey;
  const setOverBudgetAccepted = (accepted: boolean) => setOverConfirmedKey(accepted ? selectionKey : null);

  /** Leftover budget is only a suggestion: it never locks Pay, the user can always spend less. */
  const mustUseBudget = false;

  useEffect(() => {
    patchAiCampaignDraft({ campaignName, couponCode });
  }, [campaignName, couponCode]);


  /** The orientations the plan's screens come in. Each gets an upload slot. */
  const required = planOrientations;
  const uploadOrientations = required;

  // One creative is enough to launch. If a plan covers both orientations and only one is uploaded,
  // that creative also plays on the other screens (the backend falls back to it), so the user is
  // warned the content may not fit, not blocked.
  const uploadedCount = uploadOrientations.filter((o) =>
    Boolean(uploads[o]?.mediaId),
  ).length;
  const missingRequired = required.filter((o) => !uploads[o]?.mediaId);
  const hasCreative = uploadedCount > 0;

  // If the plan covers an orientation that has no content, the first press on Pay only shows a
  // warning on the bill; pressing it again goes ahead. Uploading or removing content resets this.
  const uploadedOrientations = uploadOrientations.filter((o) => Boolean(uploads[o]?.mediaId));
  const creativeKey = `${uploadedOrientations.join(',')}/${missingRequired.join(',')}`;
  const [warnedKey, setWarnedKey] = useState<string | null>(null);
  const needsCreativeWarning = hasCreative && missingRequired.length > 0;
  const creativeWarningShown = needsCreativeWarning && warnedKey === creativeKey;
  const namesOf = (list: AiPreviewOrientation[]) => list.map((o) => o.toLowerCase()).join(' and ');
  const creativeWarning = creativeWarningShown
    ? `Your content might not fit ${namesOf(missingRequired)} screens. We recommend uploading ${namesOf(missingRequired)} content too. Tap “Pay anyway” to continue with your ${namesOf(uploadedOrientations)} content on all screens.`
    : null;
  const payable = Math.max(0, cost);
  const balanceOk = availableBalance >= payable;
  const shortfall = Math.max(0, payable - availableBalance);
  const overBudget = planBudget != null && cost > planBudget + 0.005;
  // The orientation re-plan runs straight away; screens and plays are saved when Pay is pressed.
  const orientationPending = customisation.orientationsChanged;
  const canLaunch =
    hasCreative &&
    !orientationPending &&
    (!overBudget || overBudgetAccepted) &&
    !mustUseBudget &&
    !isUploading &&
    payable > 0 &&
    payable <= MAX_CAMPAIGN_AMOUNT;

  const slots = current.timeSlots ?? [];
  const visibleSlots = showAllSlots ? slots : slots.slice(0, 8);
  const dummyForm = emptyManualForm();

  const launchBlockedReason = orientationPending
    ? 'Finding the best screens…'
    : mustUseBudget
      ? `Use your remaining ${formatMoney(unusedBudget ?? 0)} to continue`
    : overBudget && !overBudgetAccepted
      ? `Tick the box to pay the extra ${formatMoney(cost - (planBudget ?? 0))} and continue`
      : !hasCreative
    ? 'Upload your ad content to launch'
    : cost > MAX_CAMPAIGN_AMOUNT
        ? `Maximum order is ${formatMoney(MAX_CAMPAIGN_AMOUNT)}. Reduce the plan to continue.`
        : !balanceOk
          ? `Need ${formatMoney(shortfall)} more — pay and launch in one step`
          : null;

  const [regenReason, setRegenReason] = useState<'orientation' | 'screens'>('orientation');

  /**
   * Adds or removes an orientation. The backend re-plans (and re-prices) the whole campaign for
   * what is selected: one orientation, or "BOTH" for landscape and portrait screens together.
   * At least one orientation always stays selected.
   */
  const handleOrientationChange = (toggled: AiPreviewOrientation) => {
    if (regenerate.isPending) return;
    const previous = customisation.orientations;
    const next = previous.includes(toggled)
      ? previous.filter((o) => o !== toggled)
      : (['LANDSCAPE', 'PORTRAIT'] as AiPreviewOrientation[]).filter((o) => o === toggled || previous.includes(o));
    if (!next.length) return;

    customisation.setOrientations(next);
    setRegenReason('orientation');

    const payload = {
      ...(current.payload || {}),
      screenOrientation: next.length === 2 ? 'BOTH' : next[0],
      couponCode: activeCouponCode,
    };
    regenerate.mutate(payload, {
      onSuccess: (res) => {
        if (!res?.data) {
          customisation.setOrientations(previous);
          toast.error(res?.message || 'No screens found for that orientation');
          return;
        }
        const plan = { ...res.data, payload, aiRecommendedOrientation: aiOrientation };
        setCurrent(plan);
        onAiDataChange?.(plan);

        const got = new Set((res.data.requiredOrientation ?? []).map((o) => String(o).toUpperCase()));
        const missing = next.filter((o) => !got.has(o));
        if (missing.length) {
          // Show what the plan really contains, so the picker never claims an orientation it lacks.
          const present = next.filter((o) => got.has(o));
          if (present.length) customisation.setOrientations(present);
          toast.info(`No ${missing.join(' or ').toLowerCase()} screens were free for your brief, so the plan uses the rest.`);
        }
      },
      onError: () => customisation.setOrientations(previous),
    });
  };

  /**
   * Saves the chosen screens and plays on the AI draft through the existing step2 + step3
   * endpoints, so the server prices and stores exactly that selection (as the manual builder does).
   * It runs when the user presses Pay, not on every click, so choices are never interrupted.
   */
  const saveSelection = useMutation({
    mutationFn: async (input: {
      campaignId: string;
      slots: ReturnType<typeof bookingSlotsForPlays>;
    }) => {
      const billboardIds = [...new Set(input.slots.map((slot) => slot.billboardId))];
      await campaignStep2({ campaignId: input.campaignId, billboardIds });
      return campaignStep3({
        campaignId: input.campaignId,
        bookingSlots: input.slots,
        ...(activeCouponCode ? { couponCode: activeCouponCode } : {}),
      });
    },
  });
  const repricing = saveSelection.isPending;
  /** Set while a Pay click is waiting for the save to finish, so the payment continues after it. */
  const launchAfterSave = useRef(false);

  /** Saves the user's choices; resolves true when the server accepted them. */
  const saveSelectionNow = async (): Promise<boolean> => {
    const campaignId = String(current.campaignId || current.preview?.campaignId || '');
    const chosen = screens.filter((s) => s._id && customisation.selectedKeys.has(aiScreenKey(s)));
    if (!campaignId || !chosen.length) return false;

    // Ask for free plays right now, so the booking only requests plays that are really free.
    let freshCapacity = capacity ?? {};
    try {
      const res = await fetchScreenCapacity({
        billboardIds: chosen.map((s) => String(s._id)),
        dates,
        customDuration: adSeconds,
      });
      freshCapacity = { ...freshCapacity, ...(res.data?.capacity ?? {}) };
    } catch {
      // Fall back to the capacity we already have; the server still has the final say.
    }

    const playsById: Record<string, number> = {};
    for (const screen of chosen) {
      playsById[String(screen._id)] = customisation.playsByKey[aiScreenKey(screen)] ?? 0;
    }
    const slots = bookingSlotsForPlays(current, playsById, freshCapacity);
    if (!slots.length) {
      toast.error('No free plays are left on the screens you chose.');
      return false;
    }
    const requested = Object.values(playsById).reduce((sum, n) => sum + n, 0) * dates.length;
    const placed = slots.reduce((sum, slot) => sum + slot.repetitions, 0);

    setRegenReason('screens');
    try {
      const res = await saveSelection.mutateAsync({ campaignId, slots });
      if (!res?.data) {
        toast.error(res?.message || 'Could not confirm your plan');
        return false;
      }
      const plan = applyStep3ToPlan(current, slots, res.data, planBudget);
      setCurrent(plan);
      onAiDataChange?.(plan);
      // If the server did not apply the coupon (for example it ran out), say so and drop it, so the
      // total the user sees is the total they will pay.
      if (activeCouponCode && !String(res.data.coupon?.couponCode ?? '').trim()) {
        toast.info(`${activeCouponCode} could not be applied, so it was removed.`);
        setCouponCode('');
      }
      if (placed < requested) {
        toast.info('Some plays were not free, so we booked the ones that are.');
      }
      return true;
    } catch (error) {
      const message = getErrorMessage(error, '');
      toast.error(
        /insufficient capacity/i.test(message)
          ? 'Some of those plays are no longer free. Please lower the plays and try again.'
          : message || 'Could not confirm your plan. Please try again.',
      );
      return false;
    }
  };

  // ── Offers: picking one is instant and local; the server applies it when Pay is pressed ──
  const offers = rankOffers(couponsQuery.data ?? [], estimate.subtotal);
  const bestOffer = offers.find((offer) => offer.discount > 0) ?? null;

  const applyOffer = (code: string) => setCouponCode(code.trim().toUpperCase());
  const removeOffer = () => setCouponCode('');

  /** Applies a typed code. Returns a short message when it cannot be used, otherwise null. */
  const applyTypedCode = (typed: string): string | null => {
    const code = typed.trim().toUpperCase();
    if (!code) return 'Enter a code';
    const offer = offers.find((o) => o.code === code);
    if (!offer) return 'That code is not valid for this order';
    if (offer.discount <= 0) return `Add ${formatMoney(offer.unlockNeeds)} more to use ${code}`;
    applyOffer(code);
    return null;
  };

  const handleCampaignNameChange = (value: string) => {
    setCampaignName(sanitizeCampaignName(value));
  };

  const handleUploaded = (orientation: AiPreviewOrientation, item: CampaignMediaItem) => {
    setUploads((prev) => ({ ...prev, [orientation]: item }));
    if (!item.mediaUrl) return;
    setPreviewByOrientation((prev) => {
      const existing = prev[orientation];
      if (existing?.url) return prev;
      return {
        ...prev,
        [orientation]: {
          url: item.mediaUrl || '',
          type: item.mediaType || '',
        },
      };
    });
  };

  const handleCleared = (orientation: AiPreviewOrientation) => {
    setUploads((prev) => {
      const next = { ...prev };
      delete next[orientation];
      return next;
    });
  };

  const handlePreviewChange = (
    orientation: AiPreviewOrientation,
    updater: CreativePreview | ((prev: CreativePreview) => CreativePreview),
  ) => {
    setPreviewByOrientation((prev) => {
      const currentPreview =
        prev[orientation] || ({ url: '', type: '' } as CreativePreview);
      const next =
        typeof updater === 'function' ? updater(currentPreview) : updater;
      return { ...prev, [orientation]: next };
    });
  };

  const buildCreativePayload = () => {
    const uploaded = uploadOrientations
      .map((o) => uploads[o])
      .filter(Boolean) as CampaignMediaItem[];

    const galleryIds = uploaded
      .map((m) => m.galleryId)
      .filter((id): id is string => Boolean(id));
    const sharedGalleryId =
      galleryIds.length > 0 &&
      uploaded.every((m) => m.galleryId && m.galleryId === galleryIds[0])
        ? galleryIds[0]
        : undefined;

    return creativeSourcePayload({
      galleryId: sharedGalleryId,
      mediaIds: uploaded.map(({ mediaId, orientation }) => ({
        mediaId,
        orientation: orientation || 'LANDSCAPE',
      })),
    });
  };

  const handleLaunch = () => {
    const creative = buildCreativePayload();
    if (!('galleryId' in creative) && !('mediaIds' in creative)) {
      toast.error('Please upload your ad content');
      return;
    }
    if (cost > MAX_CAMPAIGN_AMOUNT) {
      toast.error(
        `Maximum campaign order is ${formatMoney(MAX_CAMPAIGN_AMOUNT)}. Reduce the plan to continue.`,
      );
      return;
    }

    const campaignId = String(
      current.campaignId || current.preview?.campaignId || '',
    );
    if (!campaignId) {
      toast.error('Missing campaign id from AI plan');
      return;
    }

    const runLaunch = () => {
      onCampaignCompleted?.();
      save.mutate(
        {
          campaignId,
          ...creative,
          campaignName: campaignName.trim() || 'My AI Campaign',
          couponCode: activeCouponCode,
          ...(formData.agencyClientId
            ? { agencyClientId: formData.agencyClientId }
            : {}),
        },
        {
          onSuccess: (res) => {
            toast.success(res?.message || 'Campaign launched');
            onLaunchSuccess(res?.data?.campaignId || campaignId);
          },
        },
      );
    };

    if (shortfall > 0) {
      if (isTeamMember) {
        toast.error(
          'Insufficient agency wallet balance. Ask your agency admin to add funds.',
        );
        return;
      }
      setDepositOpen(true);
      return;
    }

    runLaunch();
  };

  const handleDepositSuccess = () => {
    setDepositOpen(false);
    toast.success('Payment successful. Launching campaign…');

    const creative = buildCreativePayload();
    const campaignId = String(
      current.campaignId || current.preview?.campaignId || '',
    );
    if (
      !campaignId ||
      (!('galleryId' in creative) && !('mediaIds' in creative))
    ) {
      toast.error('Missing campaign details after payment');
      return;
    }

    onCampaignCompleted?.();
    save.mutate(
      {
        campaignId,
        ...creative,
        campaignName: campaignName.trim() || 'My AI Campaign',
        couponCode: activeCouponCode,
        ...(formData.agencyClientId
          ? { agencyClientId: formData.agencyClientId }
          : {}),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message || 'Campaign launched');
          onLaunchSuccess(res?.data?.campaignId || campaignId);
        },
      },
    );
  };

  /** Pay: first save any screens or plays the user changed, then continue to payment. */
  const handlePay = async () => {
    if (needsCreativeWarning && !creativeWarningShown) {
      setWarnedKey(creativeKey);
      return;
    }
    if (!needsSave) {
      handleLaunch();
      return;
    }
    launchAfterSave.current = true;
    if (!(await saveSelectionNow())) launchAfterSave.current = false;
  };

  // Once the save lands, continue to payment with the server's final price.
  useEffect(() => {
    if (!launchAfterSave.current || needsSave || repricing) return;
    launchAfterSave.current = false;
    if (canLaunch) {
      // Deferred a tick: starting the payment sets state, which should not happen inside the effect.
      window.setTimeout(handleLaunch, 0);
    } else if (overBudget && !overBudgetAccepted) {
      toast.info(
        `Your final price is ${formatMoney(cost)}, ${formatMoney(cost - (planBudget ?? 0))} above your budget. Tick the box to pay the difference, or lower a play.`,
      );
    } else if (launchBlockedReason) {
      toast.info(launchBlockedReason);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSave, repricing, canLaunch, cost]);

  return {
    campaignName,
    onCampaignNameChange: handleCampaignNameChange,
    dateRange,
    totalRepetitions,
    durationSec,
    totalScreens,
    utilization,
    planBudget,
    amountWithTax,
    amountWithoutTax,
    remaining,
    appliedDiscount,
    taxRows,
    isCouponRefreshing: regenerate.isPending,
    isRepricing: repricing,
    days,
    addableScreens,
    capacityTotals,
    budgetSuggestions,
    mustUseBudget,
    hasUnusedBudget,
    unusedAccepted,
    setUnusedAccepted,
    addableLoading: catalogQuery.isLoading,
    addingScreenId,
    addScreen,
    estimatePlayPrice,
    remainingBudget,
    unusedBudget,
    canFillBudget,
    fillPlan,
    fillRemainingBudget,
    equalizePlays,
    onOrientationChange: handleOrientationChange,
    creativeWarning,
    regenReason,
    needsSave,
    offers,
    bestOffer,
    offersLoading: couponsQuery.isLoading,
    appliedCouponCode: activeCouponCode,
    couponSaving: estimate.discount,
    applyOffer,
    removeOffer,
    applyTypedCode,
    screens,
    slots,
    visibleSlots,
    showAllSlots,
    setShowAllSlots,
    screenNameById,
    namesForSlot,
    fallbackScreenNames,
    required,
    uploadOrientations,
    missingRequired,
    uploads,
    previewByOrientation,
    onUploaded: handleUploaded,
    onCleared: handleCleared,
    onPreviewChange: handlePreviewChange,
    dummyForm,
    cost,
    estimate,
    setIsUploading,
    isUploading,
    customisation,
    aiScreenKey,
    aiOrientation,
    overBudget,
    overBudgetAmount: overBudget ? cost - (planBudget ?? 0) : 0,
    overBudgetAccepted,
    setOverBudgetAccepted,
    payable,
    availableBalance,
    balanceOk,
    hasCreative,
    uploadedCount,
    canLaunch,
    launchBlockedReason,
    isLaunching: save.isPending,
    needsTopUp: shortfall > 0,
    depositOpen,
    // Whole rupees: the top-up is a clean amount, and any small extra stays in the wallet.
    depositAmount: Math.ceil(shortfall),
    launchShortfall: shortfall,
    closeDeposit: () => setDepositOpen(false),
    handleDepositSuccess,
    onLaunch: handlePay,
  };
}
